import React, { useState, useEffect } from "react";
import {
  Search,
  Calendar,
  Package,
  MessageSquare,
  FileText,
  Phone,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useTheme } from "../../../../context/ThemeContext";
import { getAllUsers } from "../../../api";

const Pricing = () => {
  const { isDarkTheme } = useTheme();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("id"); // "id" or "account"
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);

  // API to load user data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        if (response.status === "success") {
          setUsers(response.users_list);
        }
      } catch (error) {
        console.error("Failed to fetch users", error);
      }
    };
  
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((user) =>
    searchType === "id"
      ? user.id.toString().includes(searchTerm)
      : user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Function to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Helper to render Payment Details based on rules
  const renderPaymentDetails = (user) => {
    // For Basic subscription, do not show any payment details.
    if (user.subscription === "Basic") {
      return <span>N/A</span>;
    }

    // Only allow payment details for Visa, MasterCard, or Jazz Cash.
    const allowedProviders = ["Visa", "MasterCard", "Jazz Cash"];
    const provider = allowedProviders.find((prov) =>
      user.paymentMethod.includes(prov)
    );
    if (!provider) {
      return <span>N/A</span>;
    }

    // Just display the provider name.
    const displayProvider = provider === "Jazz Cash" ? "JazzCash" : provider;
    return (
      <span className={isDarkTheme ? "text-gray-300" : "text-gray-600"}>
        {displayProvider}
      </span>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent`}>
            Subscription & Pricing
          </h1>
          <p className={`text-sm mt-1 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
            Manage user subscriptions and view usage metrics
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className={`relative w-full sm:w-80 ${isDarkTheme ? "text-white" : "text-gray-900"}`}>
            <input
              type="text"
              placeholder={`Search by ${searchType === "id" ? "ID" : "name or email"}...`}
              className={`pl-10 pr-4 py-2 w-full rounded-lg border ${
                isDarkTheme ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200`}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
          </div>

          <div className="relative w-full md:w-52">
            <select
              className={`w-full py-2 px-3 pr-8 rounded-lg border appearance-none ${
                isDarkTheme ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
              } focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-200`}
              value={searchType}
              onChange={(e) => {
                setSearchType(e.target.value);
                setSearchTerm("");
              }}
            >
              <option value="id">Search by ID</option>
              <option value="account">Search by Account</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
              width="20"
              height="20"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.29a.75.75 0 010-1.08z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* User Subscription Details */}
      <div className="mt-10">
        <h2 className={`text-lg font-bold mb-4 ${isDarkTheme ? "text-white" : "text-gray-900"}`}>
          User Subscription Details
        </h2>

        {/* Table */}
        <div className={`overflow-x-auto rounded-lg border ${isDarkTheme ? "border-gray-700" : "border-gray-200"}`}>
          <table
            className={`min-w-full divide-y ${isDarkTheme ? "divide-gray-700 bg-gray-800" : "divide-gray-200 bg-white"}`}
          >
            <thead className={isDarkTheme ? "bg-gray-900" : "bg-gray-50"}>
              <tr>
                <th
                  scope="col"
                  className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                >
                  ID
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                >
                  User
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                >
                  Subscription
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                >
                  Services Used
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-left text-xs font-medium ${isDarkTheme ? "text-gray-400" : "text-gray-500"} uppercase tracking-wider`}
                >
                  Payment Details
                </th>
              </tr>
            </thead>
            <tbody
              className={`divide-y ${isDarkTheme ? "divide-gray-700" : "divide-gray-200"}`}
            >
              {currentUsers.length > 0 ? (
                currentUsers.map((user) => {
                  const displaySubscription = user.subscription;
                  return (
                    <tr key={user.id} className={`${isDarkTheme ? "hover:bg-gray-700" : "hover:bg-gray-50"} transition-colors duration-150`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={isDarkTheme ? "text-gray-300" : "text-gray-900"}>#{user.id}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-9 w-9 rounded-full bg-amber-500 flex items-center justify-center text-white font-medium`}>
                            {user.name.charAt(0)}
                          </div>
                          <div className="ml-3">
                            <div className={`text-sm font-medium ${isDarkTheme ? "text-white" : "text-gray-900"}`}>
                              {user.name}
                            </div>
                            <div className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${
                            displaySubscription === "Free"
                              ? "text-blue-500"
                              : displaySubscription === "Premium"
                              ? "text-amber-500"
                              : "text-purple-500"
                          }`}
                        >
                          {displaySubscription}
                        </div>
                        <div className={`text-xs ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                          Expires: {user.expiryDate}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="flex items-center text-sm">
                            <Package size={16} className={`mr-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`} />
                            <span className={isDarkTheme ? "text-gray-300" : "text-gray-600"}>
                              {formatNumber(user.tokens)} Tokens
                            </span>
                          </div>
                          <div className="flex items-center text-sm">
                            <MessageSquare size={16} className={`mr-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`} />
                            <span className={isDarkTheme ? "text-gray-300" : "text-gray-600"}>
                              {formatNumber(user.searches)} Web Searches
                            </span>
                          </div>
                          <div className="flex items-center text-sm">
                            <FileText size={16} className={`mr-2 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`} />
                            <span className={isDarkTheme ? "text-gray-300" : "text-gray-600"}>
                              {formatNumber(user.pdfs)} PDFs
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderPaymentDetails(user)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className={`px-6 py-8 text-center text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No users found matching your search criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="flex justify-between items-center mt-4">
            <div className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
              Showing {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${
                  currentPage === 1
                    ? isDarkTheme
                      ? "text-gray-600 bg-gray-800"
                      : "text-gray-400 bg-gray-100"
                    : isDarkTheme
                    ? "text-white hover:bg-gray-700"
                    : "text-gray-700 hover:bg-gray-100"
                } transition-colors duration-150`}
              >
                <ChevronLeft size={18} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === page
                      ? "bg-amber-500 text-white"
                      : isDarkTheme
                      ? "text-white hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  } transition-colors duration-150`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${
                  currentPage === totalPages
                    ? isDarkTheme
                      ? "text-gray-600 bg-gray-800"
                      : "text-gray-400 bg-gray-100"
                    : isDarkTheme
                    ? "text-white hover:bg-gray-700"
                    : "text-gray-700 hover:bg-gray-100"
                } transition-colors duration-150`}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pricing;
